import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Calendar, Compass, MapPin } from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { BookLink } from "../components/BookLink";
import { SERVICE_AREAS } from "../config";

const DR_LANE_IMG = "/assets/dr-lane.jpg";

const CHAPTERS = [
    {
        num: "01",
        title: "Root-Cause Healing",
        text: "Pain is a signal, not the story. Every care plan begins by understanding why your body is asking for help — then treating the cause, not chasing the symptom.",
    },
    {
        num: "02",
        title: "Customized Movement Protocols",
        text: "No two bodies move the same way. Your plan is built from your assessment, your goals, and your life — and it evolves as you do.",
    },
    {
        num: "03",
        title: "Patient-Empowered Recovery",
        text: "The best outcomes happen when patients understand their own bodies. You'll leave every visit knowing exactly what we're doing and why.",
    },
    {
        num: "04",
        title: "Long-Term Vitality",
        text: "Relief is the beginning, not the end. The goal is a resilient body that stays strong, mobile, and ready for the life you want to live.",
    },
];

export default function About() {
    useEffect(() => {
        document.title = "About Dr. Lane | Chirolane & Wellness, LLC — Move Better. Live Stronger.";
    }, []);

    return (
        <main>
            <section className="hero-grain relative bg-gradient-to-b from-blue-50/70 via-slate-50 to-white pt-32 sm:pt-40 pb-16 sm:pb-24 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
                    <div className="lg:col-span-7">
                        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-6 flex items-center gap-3">
                            <span className="h-px w-10 bg-blue-700 inline-block" /> About
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08] text-[#0A192F]">
                            <MaskedLine delay={0.2}>Meet Dr. Lane —</MaskedLine>
                            <MaskedLine delay={0.35} className="italic text-blue-700">your partner in movement</MaskedLine>
                        </h1>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.6 }}
                            className="mt-7 space-y-5 text-base sm:text-lg font-light leading-relaxed text-slate-600 max-w-2xl"
                        >
                            <p>
                                <span className="font-semibold text-[#0A192F]">Dr. Quandacia Lane, D.C., M.S.</span> is a licensed chiropractor, movement specialist, and proud United States Marine Corps veteran dedicated to helping people move better, feel stronger, and remain active throughout every stage of life.
                            </p>
                            <p>
                                Her passion for movement and human performance began with a bachelor's degree in Kinesiology–Exercise Science from The University of Texas at Arlington. She later earned a master's degree in Human Performance–Strength and Conditioning and a Doctor of Chiropractic degree from Parker University, and she is a Certified Corrective Exercise Specialist.
                            </p>
                            <p>
                                After caring for patients with a wide range of injuries and movement limitations, Dr. Lane founded Chirolane &amp; Wellness to provide a more personalized and convenient approach to care — combining chiropractic adjustments, manual therapy, corrective exercise, sports recovery, and patient education around each patient's individual needs and goals.
                            </p>
                            <p>
                                As a mother of four, veteran, and healthcare professional, Dr. Lane understands how demanding life can be. Her mobile practice brings high-quality chiropractic and wellness care directly to patients throughout the DFW area — making it easier for athletes, active adults, children, and busy families to prioritize their health.
                            </p>
                            <p className="font-display italic text-xl sm:text-2xl text-blue-800 leading-snug">
                                “Movement is one of the keys to a healthy, fulfilling life. My mission is not temporary relief — it's helping every patient move with confidence, build lasting strength, and enjoy the activities that matter most.”
                            </p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.75 }}
                            className="mt-8 flex flex-wrap gap-3"
                        >
                            {["Doctor of Chiropractic (D.C.)", "M.S. Strength & Conditioning", "USMC Veteran", "Certified Corrective Exercise Specialist"].map((b) => (
                                <span key={b} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600">
                                    <BadgeCheck className="h-4 w-4 text-blue-700" /> {b}
                                </span>
                            ))}
                        </motion.div>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5"
                    >
                        <div className="relative max-w-md mx-auto">
                            <div className="absolute -inset-4 rounded-[2.5rem] bg-blue-100/70 rotate-2" />
                            <img
                                data-testid="dr-lane-headshot"
                                src={DR_LANE_IMG}
                                alt="Dr. Lane, DC — founder of Chirolane & Wellness"
                                className="relative rounded-[2rem] aspect-[4/5] w-full object-cover shadow-2xl shadow-blue-900/20"
                            />
                            <div className="absolute -bottom-5 -right-2 sm:right-4 bg-[#0A192F] text-white rounded-2xl px-6 py-4 shadow-xl">
                                <p className="font-display italic text-lg leading-tight">Move Better.<br />Live Stronger.</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Marquee dark items={["Move Better. Live Stronger.", "Root-Cause Care", "Patient-Empowered Recovery", "Long-Term Vitality"]} />

            {/* MANIFESTO CHAPTERS */}
            <section className="py-20 sm:py-28 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                    <Reveal className="max-w-2xl">
                        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-5 flex items-center gap-3">
                            <Compass className="h-4 w-4" /> The Philosophy
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0A192F] leading-tight">
                            Four principles behind <span className="italic text-blue-700">every adjustment</span>
                        </h2>
                    </Reveal>
                    <div data-testid="manifesto-chapters" className="mt-16 space-y-0 divide-y divide-slate-200 border-y border-slate-200">
                        {CHAPTERS.map((c, i) => (
                            <Reveal key={c.num} delay={i * 0.06}>
                                <div className="group grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-8 py-10 sm:py-12 items-start transition-colors duration-300 hover:bg-blue-50/40 sm:px-6 sm:-mx-6 rounded-none">
                                    <span className="sm:col-span-2 font-display italic text-5xl sm:text-6xl text-slate-200 group-hover:text-blue-200 transition-colors duration-300 select-none" aria-hidden="true">
                                        {c.num}
                                    </span>
                                    <h3 className="sm:col-span-4 font-display text-2xl sm:text-3xl font-semibold text-[#0A192F]">{c.title}</h3>
                                    <p className="sm:col-span-6 text-base font-light leading-relaxed text-slate-600">{c.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* DFW COMMITMENT */}
            <section className="py-20 sm:py-28 bg-[#0A192F] text-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
                    <Reveal className="lg:col-span-7">
                        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400 mb-5">Community</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
                            Rooted in South Dallas, <span className="italic text-sky-300">built for Texas lifestyles</span>
                        </h2>
                        <p className="mt-7 text-base sm:text-lg font-light leading-relaxed text-slate-300 max-w-2xl">
                            From Friday night lights to Kiest Park mornings, South Dallas moves — and Dr. Lane is committed to keeping it that way. Chirolane &amp; Wellness proudly serves active families, athletes, and professionals across South Dallas and surrounding communities.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-2.5">
                            {SERVICE_AREAS.map((area) => (
                                <span key={area} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-slate-300">
                                    <MapPin className="h-3 w-3 text-sky-400" /> {area}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-5">
                        <div className="rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-10 text-center">
                            <p className="font-display italic text-3xl sm:text-4xl leading-snug">“Your body was built to move. Let's help it move well.”</p>
                            <p className="mt-4 text-xs uppercase tracking-[0.25em] text-sky-300 font-semibold">Dr. Lane, DC</p>
                            <BookLink
                                data-testid="about-book-button"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-sky-400 text-[#0A192F] text-sm font-bold px-8 py-4 transition-all duration-300 hover:bg-white hover:-translate-y-0.5"
                            >
                                <Calendar className="h-4 w-4" />
                                Book an Appointment
                                <ArrowRight className="h-4 w-4" />
                            </BookLink>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
